// OpenLayers
import OlMap from "ol/Map";
import View from "ol/View";
import TileWMS from "ol/source/TileWMS";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import OSMSource from "ol/source/osm";
import proj4 from "proj4";
import Locale from "lib/interfaces/locale";
import { register } from "ol/proj/proj4";
import { fromLonLat, transformExtent, transform, get } from "ol/proj";
import { defaults as defaultControls, Attribution } from "ol/control";
import { defaults as defaultInteractions } from "ol/interaction";

// Lib
import MapToolMode from "lib/enums/maptoolmode";
import MapTypes from "lib/interfaces/maptypes";
import FeatureVariation from "lib/classes/featurevariation";
import FormatMapSpecifics from 'config/mapspecifics';

// Tools
import { fetchJsonSync, fetchJson } from "lib/utilities/network";
import { zoomToExtentScreen } from "lib/utilities/map";
import { makeUrl } from "lib/utilities/misc";
import { assetURL } from "lib/utilities/asset";
import { getToolEnabled, toolModeNames } from "lib/utilities/toolmodes";

// Lang
import { getLocaleString } from "lib/localization/vendor";

/**
	* Gets an OpenLayers map based on opus map id
	* 
	* @param mapId			-	Opus map id
	* @param clientId		-	Opus client id
*/
export function getMap(mapId:number, clientId:number, onMapLoaded:Function) {
	getMapConfig(mapId, clientId, (mapConfig:any) => {
		console.log(mapConfig);
		
		// Register projection
		proj4.defs(
			mapConfig.epsgCode,
			mapConfig.projection.proj4
		);
		register(proj4);
		
		const viewExtent:any = getExtentFromMax(mapConfig.panZoom.maxExtents, mapConfig.epsgCode, mapConfig.epsgCodeView);
		
		const map:OlMap = new OlMap({
			target: "",
			layers: getLayers(mapConfig.baseLayers, mapConfig.boundary, mapConfig.epsgCode),
			controls: defaultControls({
				zoomOptions: {
					delta: 0.2,
				},
				zoom: false,
				attribution: false
			}),
			view: new View({
				center: fromLonLat([0,0]),
				zoom: 20,
				projection: mapConfig.epsgCodeView,
				enableRotation: false,
				constrainResolution: true,
				//resolutions: mapConfig.panZoom.resolutions,
				maxZoom: 20,
				minZoom: 8
			})
		});
		const overviewMap:OlMap = !mapConfig.overviewMap.enabled ? null : new OlMap({
			target: "",
			layers: getOverviewLayers(mapConfig.overviewMap, mapConfig.boundary, mapConfig.epsgCode),
			controls: defaultControls({
				zoom: false,
				attribution: false,
				rotate: false
			}),
			view: new View({
				center: fromLonLat([0,0]),
				zoom: 19,
				maxZoom: 21,
				minZoom: 8.5,
				projection: mapConfig.epsgCodeView,
				constrainResolution: true
			})
		});
		
		// Assign the language locale object from map config
		assignLocale(mapConfig.locales);
		
		/**
			* On new locale event handler
		*/
		const onNewLocale = (isNewLocale:boolean) => {
			assignLocale(mapConfig.locales);
			
			if(isNewLocale) {
				// Get new map config with this new locale
				getMapConfig(mapId, clientId, (mapConfig:any) => {
					// views/App will handle this event
					_events.invoke("new-map-config", map, mapConfig);
				});
			}
		}
		
		_events.un("new-lang", onNewLocale); // Clear any former event
		_events.on("new-lang", onNewLocale);
		
		const variations:Map<string, FeatureVariation[]> = getFeatureVariations(mapConfig.featureVariations);
		
		// Assign the config to the map instance
		map.set("opus-config", mapConfig);
		map.set("feature-variations", variations);
		
		// Get feature variations method for reference
		_config.getFeatureVariations = ():Map<string, FeatureVariation[]> => {
			return variations;
		}
		
		// Initial map tools enabled map
		toolModeNames.forEach((modeName:string, mode:MapToolMode) => {
			_config.initialToolsEnabled.set(mode, getToolEnabled(mode, mapConfig));
		});
		
		// Zoom the map to fit its extents within the user's screen
		zoomToExtentScreen(viewExtent, map);
		
		onMapLoaded({
			map: map,
			overviewMap: overviewMap
		});
	});
}

/**
	* Get layers from config
	* 
	* @param {Array<any>} baseLayers - The base layers from the config
	* @param {any} boundary - The boundary layer from the config
	* @param {string} epsgCode - The projection of the layers
*/
function getLayers(baseLayers:Array<any>, boundary:any, epsgCode:string):TileLayer<TileWMS>[] {
	const layers:TileLayer<TileWMS>[] = [];
	const layersAdded:string[] = [];
	
	// Add the base layers
	for(let baseLayer of baseLayers) {
		// Layer already added (duplicate)
		if(layersAdded.indexOf(baseLayer.layer) > -1) continue;
		
		layers.push(
			new TileLayer({
				source: new TileWMS({
					urls: baseLayer.wmsFeeds,
					params: {
						LAYERS: baseLayer.layer,
						VERSION: "1.1.1",
						SRS: epsgCode
					},
					crossOrigin: _config.crossOrigin,
					projection: epsgCode,
					attributions: baseLayer.attribution,
				}),
				properties: {
					"code": baseLayer.layer,
					"base": true,
					"config": baseLayer,
					"default-layer": layers.length < 1
				},
				visible: false
			})
		);
		layersAdded.push(baseLayer.layer);
	}
	
	// Add the boundary layer
	if(boundary?.enabled) {
		layers.push(
			new TileLayer({
				source: new TileWMS({
					urls: boundary.wmsFeeds,
					params: {
						LAYERS: boundary.layer,
						VERSION: "1.1.1",
						SRS: epsgCode
					},
					crossOrigin: _config.crossOrigin,
					projection: epsgCode
				}),
				properties: {
					"code": boundary.layer,
					"boundary": true
				}
			})
		);
	}
	
	return layers;
}

function getOverviewLayers(overviewMap:any, boundary:any, epsgCode:string):TileLayer<TileWMS>[] {
	const layers:TileLayer<TileWMS>[] = [];
	
	// Add the base layer
	layers.push(
		new TileLayer({
			source: new TileWMS({
				urls: overviewMap.wmsFeeds,
				params: {
					LAYERS: overviewMap.layer,
					VERSION: "1.1.1",
					SRS: epsgCode
				},
				crossOrigin: _config.crossOrigin,
				projection: epsgCode
			})
		})
	);
	
	// Add the boundary layer
	if(overviewMap.showBoundary && boundary) {
		layers.push(
			new TileLayer({
				source: new TileWMS({
					urls: boundary.wmsFeeds,
					params: {
						LAYERS: boundary.layer,
						VERSION: "1.1.1",
						SRS: epsgCode
					},
					crossOrigin: _config.crossOrigin,
					projection: epsgCode
				}),
				properties: {
					"code": boundary.layer,
					"boundary": true
				}
			})
		);
	}
	
	return layers;
}

function getFeatureVariations(config:any):Map<string, FeatureVariation[]> {
	if(!config) return null;
	
	const featureVariations:Map<string, FeatureVariation[]> = new Map<string, FeatureVariation[]>();
	
	for(let drawingMode in config) {
		let rows:any[] = config[drawingMode];
		let fvs:FeatureVariation[] = [];
		
		for(let row of rows) {
			fvs.push(new FeatureVariation(row));
		}
		
		featureVariations.set(drawingMode, fvs);
	}
	
	return featureVariations;
}

/**
	* Get extent from max extents in config
	* 
	* @param {any} maxExtents - Maximum extents (top, bottom, left, right)
	* @param {string} fromProj - From projection (EPSG code)
	* @param {string} toProj - To projection (EPSG code)
*/
function getExtentFromMax(maxExtents:any, fromProj:string, toProj:string) {
	return transformExtent(
		[maxExtents.left, maxExtents.bottom, maxExtents.right, maxExtents.top],
		fromProj,
		toProj
	)
}

/**
	* Assign the application's locale object based on the map config
*/
function assignLocale(locales:Locale[]) {
	if(!locales) return;
	
	locales.forEach((locale:Locale) => {
		if(locale.code == _config.languageId) {
			_config.lang.locale = locale;
		}
	})
}

/**
	* Format map config with any extra attributes
*/
function formatConfig(mapConfig:any):Promise<any> {
	return new Promise((resolve, reject) => {
		const epsgCode:string = `EPSG:${mapConfig.projection.code}`;
		const epsgCodeView:string = "EPSG:3857";
		
		// Store the epsg codes into the config for reference
		mapConfig.epsgCode = epsgCode;
		mapConfig.epsgCodeView = epsgCodeView;
		
		/** Assign dummy legend layers **/
		mapConfig.legend.layerGroups.unshift(
			{
				id: -12700,
				name: getLocaleString("omvlayergroup"),
				colour: "#000",
				isHidden: false,
				legendOrder: 0,
				layers: [
					{
						id: _config.rastersLegendId,
						name: getLocaleString("ri_layer"),
						images: [assetURL("assets/images/rasters.png")],
						notVisibleOnStart: true,
						legendOrder: 0
					},
					{
						id: _config.annotationLegendId,
						name: getLocaleString("an_layer"),
						images: [assetURL("assets/images/annotation.png")],
						legendOrder: 1
					}
				]
			}
		)
		
		// Depending on the specific map
		FormatMapSpecifics(mapConfig);
		
		/** Feature variations default settings **/
		if(_config.featureVariationsSource) {
			fetchJson(_config.featureVariationsSource)
			.then((featureVariations:any) => {
				
				if(!featureVariations?.Polygon || !featureVariations?.Point || !featureVariations?.LineString)
					return reject(new Error("Invalid feature variations"));
				
				mapConfig.featureVariations = featureVariations;
				resolve(mapConfig);
			})
			.catch((e:any) => {
				reject(new Error("Feature variations catch error: " + e));
			});
		}else{
			mapConfig.featureVariations = mapConfig.featureVariations ?? {
				"Polygon": [
					{
						id: "polygon", 
						name: "Polygon", 
						colorHex: "#0099FF", 
						description: getLocaleString("fv_polygon"), 
						hideLabel: true,
						isDefault: true
					},
					{
						id: "site", 
						name: "Site", 
						colorHex: "#33C1FF", 
						description: getLocaleString("fv_site"), 
						isDefault: true
					},
					{
						id: "housing", 
						name: "Housing", 
						colorHex: "#DD8623", 
						description: getLocaleString("fv_housing"), 
					},
					{
						id: "employment", 
						name: "Employment", 
						colorHex: "#8A06EC", 
						description: getLocaleString("fv_employment"), 
					},
				],
				"Point": [
					{
						id: "point", 
						name: "Point", 
						colorHex: "#0099FF", 
						description: getLocaleString("fv_point"),
						hideLabel: true,
						isDefault: true
					},
					{
						id: "accesspoint", 
						name: "Access point", 
						colorHex: "#29EC06", 
						description: getLocaleString("fv_accesspoint")
					}
				],
				"LineString": [
					{
						id: "linestring", 
						name: "Line", 
						colorHex: "#0099FF", 
						description: getLocaleString("fv_linestring"),
						hideLabel: true,
						isDefault: true
					},
					{
						id: "vehicleroute", 
						name: "Vehicle route", 
						colorHex: "#065AEC", 
						description: getLocaleString("fv_vroute"),
					},
					{
						id: "pedestrianroute", 
						name: "Pedestrian route", 
						colorHex: "#0DDD3F", 
						description: getLocaleString("fv_proute"),
					}
				]
			};
			
			resolve(mapConfig);
		}
	});
}

/**
	* Get map config async function
	* @param {number} mapId
	* @param {number} clientId
	* @param {Function} onLoaded
*/
export function getMapConfig(mapId:number, clientId:number, onComplete:Function) {
	const endpoint:string = !_config.previewOrigin ? 'https://opus4.co.uk/api/v1' : _config.previewOrigin;
	const url:string = makeUrl(`${endpoint}/map-configs/${mapId}`, new Map<string, any>([
		["c", clientId],
		["l", _config.languageId]
	]));
	
	fetchJson(url)
	.then((mapConfig:any) => {
		formatConfig(mapConfig)
		.then((formattedConfig:any) => onComplete(formattedConfig))
		.catch((error:Error) => {
			onComplete(mapConfig); // Still complete
			console.error(error);
		});
	});
}